import React, { useState } from "react";
import { auth } from "../../firebase";
import { signInWithPhoneNumber, signOut } from "firebase/auth";
import { ensureRecaptcha } from "../../firebase";
import { authApi } from "../../api";
import { useI18n } from "../../i18n";

export default function Signup({ onSuccess, switchToLogin }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    companyName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [step, setStep] = useState("details");
  const [otpSent, setOtpSent] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const isValidEmail = (v) => {
    const val = String(v || '').trim();
    if (!val) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val);
  };
  const isValidPhone = (v) => {
    const val = String(v || '').trim();
    if (!val) return false;
    if (val.startsWith('+')) return true; // allow E.164; server will normalize
    return /^\d{10}$/.test(val);
  };

  // If redirected from login due to unverified, prefill and auto trigger OTP
  React.useEffect(() => {
    try {
      const trigger = sessionStorage.getItem('tp_verify_trigger');
      if (trigger === '1') {
        const phone = sessionStorage.getItem('tp_verify_phone') || '';
        const email = sessionStorage.getItem('tp_verify_email') || '';
        const name = sessionStorage.getItem('tp_verify_name') || '';
        const companyName = sessionStorage.getItem('tp_verify_companyName') || '';
        if (phone || email) {
          setForm((s) => ({
            ...s,
            phone: phone || s.phone,
            email: email || s.email,
            name: name || s.name,
            companyName: companyName || s.companyName,
            // do not prefill password
          }));
          // clear flags and auto-start OTP
          sessionStorage.removeItem('tp_verify_trigger');
          setTimeout(() => {
            sendOtp().catch(() => {});
          }, 0);
        }
      }
    } catch {}
  }, []);

  const sendOtp = async () => {
    setError("");
    setLoading(true);
    try {
      if (!isValidPhone(form.phone)) {
        throw new Error('Enter a valid 10-digit phone number');
      }
      if (!isValidEmail(form.email)) {
        throw new Error('Enter a valid email address');
      }
      if (!form.password || form.password !== form.confirmPassword) {
        throw new Error(t('signup.password.mismatch'));
      }
      const signupRes = await authApi.signup({ phone: form.phone, name: form.name, companyName: form.companyName, email: form.email, password: form.password });
      // If backend indicates existing but unverified, proceed to OTP
      if (signupRes && signupRes.needsVerification) {
        if (signupRes.phone && !form.phone) onChange('phone', signupRes.phone);
      }
      const verifier = ensureRecaptcha();
      const e164 = form.phone.startsWith("+") ? form.phone : `+91${form.phone}`;
      const conf = await signInWithPhoneNumber(auth, e164, verifier);
      setConfirmation(conf);
      setOtpSent(true);
      setStep("otp");
    } catch (e) {
      setError(e?.message || "Failed to start signup");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    setLoading(true);
    try {
      if (!confirmation) throw new Error("OTP not initiated");
      await confirmation.confirm(code);
      await authApi.verify({ phone: form.phone });
      const username = form.email || form.phone;
      await authApi.login({ username, password: form.password });
      await signOut(auth);
      onSuccess?.();
    } catch (e) {
      setError(e?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-green-100 rounded-2xl p-5 shadow-sm">
      {step === "details" && (
        <>
          <div className="grid gap-3">
            <input className={`border rounded px-3 py-2 ${form.phone && !isValidPhone(form.phone) ? 'border-red-400' : ''}`} placeholder={t('signup.phone.placeholder')} value={form.phone} onChange={(e) => onChange("phone", e.target.value)} />
            <input className="border rounded px-3 py-2" placeholder={t('signup.name.placeholder')} value={form.name} onChange={(e) => onChange("name", e.target.value)} />
            <input className="border rounded px-3 py-2" placeholder={t('signup.business.placeholder')} value={form.companyName} onChange={(e) => onChange("companyName", e.target.value)} />
          
            <input className={`border rounded px-3 py-2 ${form.email && !isValidEmail(form.email) ? 'border-red-400' : ''}`} type="email" placeholder={t('signup.email.placeholder')} value={form.email} onChange={(e) => onChange("email", e.target.value)} />
            <div className="relative">
              <input
                className="border rounded px-3 py-2 w-full"
                type={showPassword ? "text" : "password"}
                placeholder={t('signup.setPassword.placeholder')}
                value={form.password}
                onChange={(e) => onChange("password", e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-600"
                onClick={() => setShowPassword((s) => !s)}
              >
                {showPassword ? t('signup.password.hide') : t('signup.password.show')}
              </button>
            </div>
            <div className="relative">
              <input
                className={`border rounded px-3 py-2 w-full ${form.confirmPassword && form.password !== form.confirmPassword ? 'border-red-400' : ''}`}
                type={showConfirm ? "text" : "password"}
                placeholder={t('signup.confirmPassword.placeholder')}
                value={form.confirmPassword}
                onChange={(e) => onChange("confirmPassword", e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-600"
                onClick={() => setShowConfirm((s) => !s)}
              >
                {showConfirm ? t('signup.password.hide') : t('signup.password.show')}
              </button>
            </div>
            {form.confirmPassword && form.password !== form.confirmPassword && (
              <div className="text-red-600 text-xs">{t('signup.password.mismatch')}</div>
            )}
          </div>
          {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
          <button className="mt-4 w-full bg-green-600 text-white py-2 rounded disabled:opacity-60" onClick={sendOtp} disabled={loading}>
            {loading ? t('signup.submit.loading') : t('signup.submit.idle')}
          </button>
          <div className="text-xs text-center text-gray-500 mt-3">
            {t('signup.already')} {" "}
            <button className="text-green-600 underline" onClick={switchToLogin}>{t('signup.login')}</button>
          </div>
          <div id="recaptcha-container" className="mt-2"></div>
        </>
      )}
      {step === "otp" && (
        <>
          <div className="text-sm text-gray-600 mb-2">{t('signup.otp.prompt')}</div>
          <input className="border rounded px-3 py-2 w-full" placeholder={t('signup.otp.placeholder')} value={code} onChange={(e) => setCode(e.target.value)} />
          {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
          <button className="mt-4 w-full bg-green-600 text-white py-2 rounded disabled:opacity-60" onClick={verifyOtp} disabled={loading || !otpSent}>
            {loading ? t('signup.otp.submit.loading') : t('signup.otp.submit.idle')}
          </button>
        </>
      )}
    </div>
  );
}


